/**
 * Reports > Redemptions panel — the `Blotter` wired to `GET /reports/redemptions`.
 *
 * Mirrors `admin/contacts/AdminContactsPanel.tsx`: a `makeUseRedemptionsData` factory reports
 * `isError` and `filterOptions`/`unfilteredTotal` upward through `useEffect`, because
 * `BlotterData` has no channel for either. `Blotter.tsx` is shared by 11 surfaces; its only change
 * for this screen is the generic `exportDownload` hook (server-built CSV instead of client rows).
 */
import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import { Alert, Button, Snackbar } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { downloadRedemptionsCsv } from "@/api/reportsRedemptions";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import ColumnConfigPopover from "@/components/ColumnConfigPopover";
import { buildColumns } from "@/components/reports/redemptionsColumns";
import WidgetEmptyState from "@/components/WidgetEmptyState";
import {
  COLS_PARAM,
  COLUMN_LABELS,
  DEFAULT_HIDDEN,
  DEFAULT_ORDER,
  FILTER_FIELD_IDS,
  FILTER_LABELS,
  MIN_WIDTH,
  PAGE_SIZE,
  REDEMPTIONS_TEXT,
  STATUS_FILTER_ORDER,
} from "@/constants/reportsRedemptions";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";
import { REDEMPTIONS_QUERY_KEY, useReportsRedemptions } from "@/hooks/useReportsRedemptions";
import type { RedemptionRow, RedemptionsFilterOptions } from "@/interfaces/reportsRedemptions";
import { formatDate } from "@/utils/date-format";
import {
  pickEmptyMessage,
  redemptionStatusDisplay,
  toListParams,
  toRedemptionsExportParams,
  toSortBy,
} from "@/utils/reportsRedemptions-helper";

/** The Redemption Status filter's options — the screen's own labels, not the raw wire values. */
const STATUS_OPTIONS = STATUS_FILTER_ORDER.map((status) => ({
  value: redemptionStatusDisplay(status),
  label: redemptionStatusDisplay(status),
}));

/** "Jun 1 – Jun 30" / "Jun 1 – …" / "… – Jun 30", for the two daterange filter chips. */
function dateRangeChipLabel(label: string, values: string[]): string {
  const [from, to] = values;
  const fromText = from ? formatDate(from) : "…";
  const toText = to ? formatDate(to) : "…";
  return `${label}: ${fromText} – ${toText}`;
}

/**
 * Builds the redemptions blotter's data hook — a factory rather than a plain module-level hook,
 * same reason as `AdminContactsPanel.makeUseContactsData`: `BlotterData` cannot carry an error
 * flag or the filter option lists, and both now ride on the list response itself.
 */
function makeUseRedemptionsData(
  onError: (failed: boolean) => void,
  onFilterOptions: (options: RedemptionsFilterOptions) => void,
  onUnfilteredTotal: (total: number) => void,
) {
  return function useRedemptionsData(q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<RedemptionRow> {
    const { data, isLoading, isError } = useReportsRedemptions({
      ...toListParams(q),
      sortBy: toSortBy(q.sortBy),
      sortDir: q.sortDir,
      // Only the table's own request asks for them; the CSV export does not.
      includeFilterOptions: true,
    });

    // `onError`/`onFilterOptions`/`onUnfilteredTotal` are deliberately not dependencies: they are
    // the factory's parameters, not reactive values, and React re-runs the newest closure whenever
    // the watched value changes — so they are never stale (Biome's useExhaustiveDependencies
    // rejects outer-scope bindings here).
    useEffect(() => {
      onError(isError);
    }, [isError]);

    const filterOptions = data?.filterOptions;
    useEffect(() => {
      if (filterOptions) onFilterOptions(filterOptions);
    }, [filterOptions]);

    const unfilteredTotal = data?.unfilteredTotal;
    useEffect(() => {
      if (unfilteredTotal !== undefined) onUnfilteredTotal(unfilteredTotal);
    }, [unfilteredTotal]);

    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };
}

/**
 * Writes `url` to the clipboard, resolving to whether it actually landed. Copied from
 * `AdminContactsPanel.writeToClipboard` — both failure modes (denied access, missing
 * `navigator.clipboard` in a non-secure context) are silent otherwise.
 */
async function writeToClipboard(url: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

export default function RedemptionsPanel() {
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [listFailed, setListFailed] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const [filterOptions, setFilterOptions] = useState<RedemptionsFilterOptions | null>(null);
  const [unfilteredTotal, setUnfilteredTotal] = useState<number | undefined>(undefined);
  const queryClient = useQueryClient();
  const prefs = useColumnPrefs({
    paramName: COLS_PARAM,
    allIds: DEFAULT_ORDER,
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
  });

  // Resolved here because the cell that triggers it is a plain renderer and may not call hooks.
  const handleCopy = useCallback((url: string) => {
    writeToClipboard(url).then((ok) => {
      setCopyFailed(!ok);
      setCopyOpen(true);
    });
  }, []);

  /** Retry invalidates the whole redemptions list branch; `isError` clears itself on success. */
  const handleRetry = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: REDEMPTIONS_QUERY_KEY });
  }, [queryClient]);

  const useRedemptionsData = makeUseRedemptionsData(
    setListFailed,
    setFilterOptions,
    setUnfilteredTotal,
  );

  const config: BlotterConfig<RedemptionRow> = {
    searchPlaceholder: REDEMPTIONS_TEXT.searchPlaceholder,
    itemNoun: REDEMPTIONS_TEXT.itemNoun,
    emptyMessage: REDEMPTIONS_TEXT.emptyMessage,
    minWidth: MIN_WIDTH,
    pageSize: PAGE_SIZE,
    defaultSortKey: "sentAt",
    defaultSortDir: "desc",
    rowKey: (r) => r.id,
    useData: useRedemptionsData,
    columns: buildColumns(prefs, { onCopy: handleCopy }),
    filterFields: [
      {
        id: FILTER_FIELD_IDS.campaign,
        label: FILTER_LABELS.campaign,
        options: filterOptions?.campaigns ?? [],
      },
      { id: FILTER_FIELD_IDS.status, label: FILTER_LABELS.status, options: STATUS_OPTIONS },
      {
        id: FILTER_FIELD_IDS.sent,
        label: FILTER_LABELS.sent,
        options: [],
        kind: "daterange",
        chipLabel: (values) => dateRangeChipLabel(FILTER_LABELS.sent, values),
      },
      {
        id: FILTER_FIELD_IDS.redeemed,
        label: FILTER_LABELS.redeemed,
        options: [],
        kind: "daterange",
        chipLabel: (values) => dateRangeChipLabel(FILTER_LABELS.redeemed, values),
      },
    ],
    exportFilename: REDEMPTIONS_TEXT.csvFilename,
    // Server-built CSV with the table's own search, filters and sort, covering every page.
    exportDownload: (q) => downloadRedemptionsCsv(toRedemptionsExportParams(q)),
    onExportError: () => setExportFailed(true),
    headerActionPlacement: "start",
    headerAction: (
      <ColumnConfigPopover
        items={DEFAULT_ORDER.map((id) => ({ id, label: COLUMN_LABELS[id] }))}
        order={prefs.order}
        hidden={prefs.hidden}
        fixedHint={REDEMPTIONS_TEXT.popoverHint}
        onToggle={prefs.toggle}
        onMove={prefs.move}
        onReorder={prefs.reorder}
        onReset={prefs.reset}
      />
    ),
  };

  if (pickEmptyMessage({ unfilteredTotal, isError: listFailed }) === "none-yet") {
    return (
      <div className="flex border border-smoke rounded-[10px] bg-white p-4">
        <WidgetEmptyState
          icon={<AssessmentOutlined />}
          title={REDEMPTIONS_TEXT.emptyTitle}
          subtitle={REDEMPTIONS_TEXT.emptySubtitle}
        />
      </div>
    );
  }

  return (
    <>
      {/* Above the Blotter, never in place of it: replacing the table would throw away the
          search, filters, sort and page the user is retrying with. */}
      {listFailed && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {REDEMPTIONS_TEXT.retry}
            </Button>
          }
        >
          {REDEMPTIONS_TEXT.listError}
        </Alert>
      )}
      {exportFailed && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }} onClose={() => setExportFailed(false)}>
          {REDEMPTIONS_TEXT.exportError}
        </Alert>
      )}
      <Blotter config={config} />
      <Snackbar
        open={copyOpen}
        autoHideDuration={3000}
        onClose={() => setCopyOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={copyFailed ? "error" : "success"}
          variant="filled"
          onClose={() => setCopyOpen(false)}
        >
          {copyFailed ? REDEMPTIONS_TEXT.copyFailed : REDEMPTIONS_TEXT.copyToast}
        </Alert>
      </Snackbar>
    </>
  );
}
